import { useCallback, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";

import { useAppDispatch, useAppSelector } from "../../../Store";

import { BasicQueryInfoInput } from "../FeedDataCalls/GQLCalls/getFeedComponentsCall";

import { gqlActions } from "../gqlData_Reducer";
import Graph_QL, { DynamicDataObk, GqlHook_Returns, StateDataObject } from "..";
import { noop } from "lodash";
import { filterByConnection } from "../filterByConnection";

import { AnimalGroupItem } from "./AnimalDataSlices/AnimalGroupSlice";
import { CREATE_ANIMAL_GROUPS, CreateAnimalGroupResponse } from "./GQLCalls/createAnimalGroupCall";
import { AnimalGroupResponse, GET_ANIMAL_GROUPS_INFO } from "./GQLCalls/getAnimalGroupCall";
import { DELETE_ANIMAL_GROUP, DeleteAnimalGroupResponse } from "./GQLCalls/deleteAnimalGroupCall";
import { clearGroupOptions } from "../../../Store/OptionsHandlers/optionsCombinedReducer";

interface AnimalGroupDirectives {
  ifIncludeAnimalCount: boolean
}

interface AnimalGroupHelperFunctions {
  deleteAnimalGroups: (ids: Number[]) => void;
}


export const useAnimalGroups = (connection: Graph_QL.GQLCons): GqlHook_Returns<AnimalGroupItem, BasicQueryInfoInput & AnimalGroupDirectives> & AnimalGroupHelperFunctions  => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();
  const [, setCursorId] = useState(-1);

  const animalGroupState = useAppSelector(({graphQlData:{domain}}) => domain.animalGroups);
  const {fullState} = useMemo(() => {
    const state = filterByConnection(animalGroupState, Graph_QL.ToArr(connection)) as Record<string, AnimalGroupItem>
    return Object.entries(state).reduce((accumulator, [key, data]) => {
      if (data.data) {
        const value = {...data.data, domain:key}; 
        accumulator.data = accumulator.data.concat(value)
      }
      return accumulator
    },{fullState: state, data: []} as StateDataObject<AnimalGroupItem>)
  },[connection, animalGroupState]);

  const getAnimalGroupInfo = useCallback(async (input: BasicQueryInfoInput & AnimalGroupDirectives, specific?: Graph_QL.GQLCons) => {
    const specificConnection = specific ? specific : connection;
    const {ifIncludeAnimalCount, ...remainingInput} = input;
    try {
      client.query<AnimalGroupResponse>({query: GET_ANIMAL_GROUPS_INFO, variables: {...specificConnection.connection, input: remainingInput, ifIncludeAnimalCount}})
        .then((e) => {
          if (e && e.data) {
            dispatch({type: String(gqlActions.domain.animalGroups.maintained), payload: {con: specificConnection.connection, data: e.data.animalGroup.animalGroup}});
            setCursorId(e.data.animalGroup.animalGroup.at(-1)?.animal_group_id ?? -1)
          }
        }).catch((error) => {
          console.log(error);
        })
    } catch (error) {
        console.log(error);
      }
    },[client, connection, dispatch])


  
  const maintainEntries = async (changedData?: Partial<AnimalGroupItem>) => {
    const names = changedData?.data?.map((animalGroup) => (animalGroup.name));
    try {
      client.mutate<CreateAnimalGroupResponse>({mutation: CREATE_ANIMAL_GROUPS, variables: {...connection.connection, names}})
        .then((response) => {
          if (response && response.data) {
            dispatch({type: String(gqlActions.domain.animalGroups.maintained), payload: {con: connection.connection, data: response.data.animalGroup.create}})
            dispatch(clearGroupOptions());
          }
        }).catch((error) => {console.log(error)})
    } catch (error) {
      console.warn("The update failed -> no changes dispatched", error)
    }
  }

  const deleteAnimalGroups = async (entries: Number[]) => {
    try {
      const deleteResponse = await client.mutate<DeleteAnimalGroupResponse>({mutation: DELETE_ANIMAL_GROUP, variables: {...connection.connection, ids: entries}})
      if (deleteResponse.data?.animalGroup.delete.success) {
        dispatch({type: String(gqlActions.domain.animalGroups.delete), payload: {con: connection.connection, data: entries}});
        dispatch(clearGroupOptions());
      }
    } catch (error) {
      console.log(error)
    }
  }
  
  return {call: getAnimalGroupInfo, fetchMore: noop, deleteEntries: noop , data: fullState, maintainEntries, deleteAnimalGroups}
} 

